import polybool from 'polybooljs';
import classifyPoint from 'robust-point-in-polygon';
import { roundVertices } from '../../util/mergePolygons';

function distance(p1, p2) {
  return Math.sqrt(Math.pow(p1.x - p2.x, 2) + Math.pow(p1.y - p2.y, 2));
}

function inflate(obj, size = 0) {
  const inflated = { ...obj };
  inflated.height = inflated.height || size;
  inflated.width = inflated.width || size;
  inflated.depth = inflated.depth || size;
  return inflated;
}

function as3d(obj) {
  return Object.assign(
    { x: 0, y: 0, z: 0, height: 0, width: 0, depth: 0 },
    obj
  );
}

function getCenter(box) {
  return {
    x: box.x + box.width / 2,
    y: box.y + box.height / 2,
    z: box.z + box.depth / 2,
  };
}

function nanTo0(obj) {
  const nonans = Object.keys(obj).reduce((out, key) => {
    out[key] = Number.isFinite(obj[key]) ? obj[key] : 0;
    return out;
  }, {});
  return Object.assign({}, obj, nonans);
}

function fixPadding(padding) {
  const defaultPadding = {
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    front: 0,
    back: 0,
  };
  return nanTo0(Object.assign(defaultPadding, padding));
}

function pad(box, padding) {
  padding = fixPadding(padding);

  const padded = {
    x: box.x - padding.left,
    y: box.y - padding.bottom,
    z: box.z - padding.back,
    width: box.width + padding.left + padding.right,
    height: box.height + padding.bottom + padding.top,
    depth: box.depth + padding.back + padding.front,
  };
  return nanTo0(padded);
}

function collides(r1, r2, padding) {
  if (padding) {
    r1 = pad(r1, padding);
  }

  const collides2d =
    r1.x < r2.x + r2.width &&
    r1.x + r1.width > r2.x &&
    r1.y < r2.y + r2.height &&
    r1.y + r1.height > r2.y;

  // only look at depth when all the input seems to support it
  // if  (r1.z || r2.z) is false, then both have z=0 and the 2d collision has caught it anyway
  if ((r1.z || r2.z) && r1.depth && r2.depth) {
    return collides2d && r1.z < r2.z + r2.depth && r1.z + r1.depth > r2.z;
  }
  return collides2d;
}

function getCollidingRects(currentRect, rects, padding) {
  return rects.filter(rect => collides(currentRect, rect, padding));
}

// check if container contains rect
// rect can also be a point or line
function contains(container, box, padding) {
  container = pad(container, padding);
  container = as3d(container);
  box = as3d(box);
  const result =
    box.x >= container.x &&
    box.x + (box.width || 0) <= container.x + container.width &&
    box.y >= container.y &&
    box.y + (box.height || 0) <= container.y + container.height &&
    box.z >= container.z &&
    box.z + (box.depth || 0) <= container.z + container.depth;

  return result;
}

function fits(slot, item) {
  return (
    (!item.logic.floor || item.logic.wall || slot.y <= 0) &&
    slot.width >= item.width &&
    slot.height >= item.height
  );
}

function splitRectangle(outer, inner) {
  const rectangles = [];

  if (inner.y + inner.height < outer.y + outer.height) {
    // top rectangle
    rectangles.push({
      width: outer.width,
      height: outer.y + outer.height - (inner.y + inner.height),
      x: outer.x,
      y: inner.y + inner.height,
    });
  }

  if (inner.x + inner.width < outer.x + outer.width) {
    // right rectangle
    rectangles.push({
      width: outer.x + outer.width - (inner.x + inner.width),
      height: outer.height,
      x: inner.x + inner.width,
      y: outer.y,
    });
  }

  if (inner.y > outer.y) {
    // bottom rectangle
    rectangles.push({
      width: outer.width,
      height: inner.y - outer.y,
      x: outer.x,
      y: outer.y,
    });
  }

  if (inner.x > outer.x) {
    // left rectangle
    rectangles.push({
      width: inner.x - outer.x,
      height: outer.height,
      x: outer.x,
      y: outer.y,
    });
  }

  return rectangles;
}

function sliceRectangle(space, rects) {
  return rects.reduce(
    (slots, rect) => {
      const collidingRects = getCollidingRects(rect, slots);

      slots = slots.filter(slot => !collidingRects.includes(slot));

      collidingRects.forEach(rectangle => {
        let newSlots = splitRectangle(rectangle, rect);

        newSlots = newSlots.filter(newSlot =>
          slots.every(slot => !contains(slot, newSlot))
        );

        slots.push(...newSlots);
      });

      return slots;
    },
    [space]
  );
}

/*
  since some colliding areas don't actually collide,
  but the snap padding makes it appear so,
  any bad collisions yield a negative closeness
*/
function weightedCloseness(intersection, rect, target) {
  let intersectionArea = Math.abs(intersection.height * intersection.width);
  if (intersection.height < 0 || intersection.width < 0) {
    intersectionArea *= -1;
  }
  const rectArea = rect.height * rect.width;
  const targetArea = target.height * target.width;
  // care mainly about how much of rect that intersects with target,
  // and secondly give a slight weight to how much of target that intersects with rect
  const intersectionRatio =
    (intersectionArea / rectArea) * (1 + rectArea / (targetArea * 10000));
  return intersectionRatio;
}

function connectingRect(r1, r2) {
  const x = Math.min(r1.x, r2.x);
  const y = Math.max(r1.y, r2.y);
  const xx = Math.max(r1.x + r1.width, r2.x + r2.width);
  const yy = Math.min(r1.y + r1.height, r2.y + r2.height);
  return {
    x: x,
    y: y,
    width: xx - x,
    height: yy - y,
  };
}

function intersectingRect(r1, r2) {
  const x = Math.max(r1.x, r2.x);
  const y = Math.max(r1.y, r2.y);
  const xx = Math.min(r1.x + r1.width, r2.x + r2.width);
  const yy = Math.min(r1.y + r1.height, r2.y + r2.height);
  return {
    x: x,
    y: y,
    width: xx - x,
    height: yy - y,
  };
}

function closer(cand, curr) {
  return curr < cand;
}

/*
  when determining what rect is most colliding,
  consider the overlapping area of two rects a measure of collision
*/
function closestCollidingRect(target, rects, fill = 0) {
  if (rects.length === 1) {
    return rects[0];
  }
  let closest = null;
  return rects.reduce((result, rect) => {
    const intersection = intersectingRect(target, inflate(rect, fill));
    const closeness = weightedCloseness(intersection, rect, target);
    if (!result || closer(closeness, closest)) {
      closest = closeness;
      return rect;
    }
    return result;
  }, null);
}

function closestPositionInSlot(rect, slot) {
  let x, y;

  if (slot.x >= rect.x) {
    x = slot.x;
  } else if (rect.x + rect.width > slot.x + slot.width) {
    x = slot.x + slot.width - rect.width;
  } else {
    x = rect.x;
  }

  if (slot.y > rect.y) {
    y = slot.y;
  } else if (rect.y + rect.height > slot.y + slot.height) {
    y = slot.y + slot.height - rect.height;
  } else {
    y = rect.y;
  }

  return { x, y };
}

function closestPosition(rect, slots) {
  let shortestDistance;

  return slots.reduce((result, slot) => {
    const position = closestPositionInSlot(rect, slot);
    const dist = distance(position, rect);

    if (!result || dist < shortestDistance) {
      shortestDistance = dist;

      return position;
    }

    return result;
  }, null);
}

function extendsOutside(child, parent, axis) {
  if (axis) {
    const sizeProperty = axis === 'x' ? 'width' : 'height';
    return (
      child[axis] < 0 ||
      child[axis] + child[sizeProperty] > parent[sizeProperty]
    );
  }

  return (
    child.x < 0 ||
    child.x + child.width > parent.width ||
    child.y < 0 ||
    child.y + child.height > parent.height
  );
}

function surround(boxes) {
  const boxes3d = boxes.map(as3d);
  const minX = Math.min(...boxes3d.map(box => box.x));
  const minY = Math.min(...boxes3d.map(box => box.y));
  const minZ = Math.min(...boxes3d.map(box => box.z));
  return {
    x: minX,
    y: minY,
    z: minZ,
    width: Math.max(...boxes3d.map(box => box.x + (box.width || 0))) - minX,
    height: Math.max(...boxes3d.map(box => box.y + (box.height || 0))) - minY,
    depth: Math.max(...boxes3d.map(box => box.z + box.depth)) - minZ,
  };
}

function mergeKids(parent, children) {
  // fake parent at 0,0
  const fake = {
    x: 0,
    y: 0,
    z: 0,
    width: parent.width,
    height: parent.height,
    depth: parent.depth,
  };

  const all = [fake, ...children.filter(child => !contains(parent, child))];
  const out = surround(all);

  // if parent isn't placed, assume {0,0}
  out.x += parent.x || 0;
  out.y += parent.y || 0;
  out.z += parent.z || 0;
  return out;
}

function mergeSiblings(s1, s2) {
  const all = [s1, s2];
  return surround(all);
}

// +-------------------+              +-------------------+
// |         r2        |              |         r2        |
// +-------------------+   === true   +-------------------+          === false
//   |---------------|                           |---------------|
//   |       r1      |                           |       r1      |
//   +---------------+                           +---------------+
function eclipsed(r1, r2) {
  return (
    r1.y + r1.height < r2.y &&
    r1.x >= r2.x &&
    r1.x + r1.width <= r2.x + r2.width
  );
}

function closest(items, direction = 'right') {
  function closer(a, b) {
    return a.x > b.x ? 1 : a.x < b.x ? -1 : 0;
  }
  if (direction === 'right') {
    return items.sort(closer)[0];
  }
  return items.sort(closer).reverse()[0];
}

function uniformPadding(size) {
  return {
    top: size,
    bottom: size,
    left: size,
    right: size,
    front: size,
    back: size,
  };
}

function rect2poly(rect) {
  return {
    inverted: false,
    regions: [
      [
        [rect.x, rect.y],
        [rect.x, rect.y + rect.height],
        [rect.x + rect.width, rect.y + rect.height],
        [rect.x + rect.width, rect.y],
      ],
    ],
  };
}

function rects2poly(rects) {
  const polygons = rects.map(rect2poly).map(roundVertices);
  let segments = polybool.segments(polygons[0]);
  for (let i = 1; i < polygons.length; i++) {
    const seg2 = polybool.segments(polygons[i]);
    const comb = polybool.combine(segments, seg2);
    segments = polybool.selectUnion(comb);
  }
  return polybool.polygon(segments);
}

function obscured(item, observer, items) {
  /*
  if there are items in items that obscure item from observers pov, it cannot be observed
  */

  const sharedYs = connectingRect(item, observer);
  const ignored = [item.itemid, observer.itemid];
  const obscurers = getCollidingRects(sharedYs, items).filter(
    rect => !ignored.includes(rect.itemid)
  );
  if (!obscurers.length) {
    //nothings in our path
    return false;
  }

  // something is between us. now we figure out if they cover our field of view or not
  let next = sharedYs.y;
  const end = next + sharedYs.height;
  obscurers.sort((a, b) => b.y - a.y); // top to bottom so we can pop
  while (obscurers.length) {
    const curr = obscurers.pop();
    if (curr.y > next) {
      // we found a hole
      return false;
    }
    const reached = curr.y + curr.height;
    if (reached > next) {
      next = reached;
    }
    if (next >= end) {
      // we've escaped our interval without holes
      return true;
    }
  }

  return false;
}

function isInsidePolygon(polygon, point) {
  return classifyPoint(polygon, point) < 1;
}

export default {
  collides,
  contains,
  closest,
  closestCollidingRect,
  closestPosition,
  distance,
  fits,
  getCenter,
  getCollidingRects,
  connectingRect,
  intersectingRect,
  rects2poly,
  sliceRectangle,
  splitRectangle,
  extendsOutside,
  mergeKids,
  mergeSiblings,
  pad,
  surround,
  eclipsed,
  uniformPadding,
  obscured,
  isInsidePolygon,
  __test__: {
    closer,
  },
};
